import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { subInvoiceService } from '../../services/subInvoiceService';
import NativePdfViewer from '../invoice-inbox/components/NativePdfViewer';
import Icon from '../../components/AppIcon';
import Badge from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { statusVariant } from '../../utils/statusVariant';
import useTranslation from '../../hooks/useTranslation';
import { formatCurrency as fmtCAD } from '../../utils/formatCurrency';

const fmt = (val) => fmtCAD(val);

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Tax rate constants (same as InvoiceForm)
const TPS_RATE = 0.05;
const TVQ_RATE = 0.09975;

const FactureApprovalPage = () => {
  const { id: projectId, invoiceId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [facture, setFacture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolvedDocUrl, setResolvedDocUrl] = useState(null);
  const [fileName, setFileName] = useState(null);

  // Approval controls state
  const [noteValue, setNoteValue] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [pctInput, setPctInput] = useState('');
  const [applyRetention, setApplyRetention] = useState(true);
  const [retentionPct, setRetentionPct] = useState('10');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ─── Retention release mode (libération de la retenue) ──────────────────────
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [releasing, setReleasing] = useState(false);
  // null = pas encore vérifié, true/false = résultat du check quittance finale signée
  const [quittanceFinaleOk, setQuittanceFinaleOk] = useState(null);

  const latestApproval = facture?.latestApproval || facture?.approvals?.[0] || null;
  const isReleaseMode =
    facture?.status === 'partially_paid' &&
    latestApproval?.retentionReleaseRequested === true &&
    !latestApproval?.retentionReleasedAt;
  const releaseApprovedAmount = parseFloat(latestApproval?.approvedAmount) || 0;
  const releaseAlreadyPaid = facture?.totalPaid || 0;
  const releaseRetenue = parseFloat(latestApproval?.retainageAmount) || 0;
  const releaseReste = Math.round((releaseApprovedAmount - releaseAlreadyPaid) * 100) / 100;
  const factureSubcontractId = facture?.subcontractId || null;

  // Derived approval math — guarded, recomputes when approvedAmount / applyRetention / retentionPct change
  const approvedAmt = Math.round((parseFloat(approvedAmount) || 0) * 100) / 100;
  const retentionPctNum = Math.max(0, parseFloat(retentionPct) || 0);
  const retainageAmount = applyRetention
    ? Math.round(approvedAmt * retentionPctNum / 100 * 100) / 100
    : 0;
  const netPayable = Math.round((approvedAmt - retainageAmount) * 100) / 100;

  // ─── Retention applied at intake (Boîte de réception) ───────────────────────
  // When retention_included, the retenue is already the final invoice amount:
  // approval shows a READ-ONLY box and reuses the stored values as-is.
  // Entry-time retention only counts when BOTH the flag and a real % are stored.
  // Legacy rows (retention_included = true, retention_percent = null) fall back to
  // the normal editable module.
  const hasEntryRetention = facture?.retentionIncluded === true && facture?.retentionPercent != null;
  // brut = total_amount (always the full tax-inclusive invoice total)
  const grossApproved = facture?.invoiceTotalWithTax || 0;
  const intakeRetentionPct = parseFloat(facture?.retentionPercent) || 0;
  // Rule C: round the retenue to the cent BEFORE subtracting.
  const intakeRetentionAmount = Math.round(grossApproved * intakeRetentionPct / 100 * 100) / 100;
  const intakeNetPayable = Math.round((grossApproved - intakeRetentionAmount) * 100) / 100;

  // ─── Approved-amount % helper — writes the $ field ($ stays the source of truth) ──
  // Base = invoice total incl. taxes, so 100% === current default (no behaviour change).
  const invoiceTotalWithTax = facture?.invoiceTotalWithTax || 0;
  const derivedApprovedPct = invoiceTotalWithTax > 0 ? (approvedAmt / invoiceTotalWithTax) * 100 : null;
  const isActivePct = (pct) => derivedApprovedPct != null && Math.abs(derivedApprovedPct - pct) < 0.005;
  const applyApprovedPct = (raw) => {
    let display = raw;
    const num = parseFloat(raw);
    if (!isNaN(num) && num > 100) display = '100';
    if (!isNaN(num) && num < 0) display = '0';
    setPctInput(display);
    const p = Math.min(100, Math.max(0, parseFloat(display) || 0));
    // approvedAmount = round(invoiceTotalWithTax × pct/100, 2)
    setApprovedAmount(String(Math.round(invoiceTotalWithTax * p) / 100));
  };

  useEffect(() => {
    const resolveDocumentUrl = async (invoiceId) => {
      // 0) sub-invoices bucket (NEW PRIMARY — {projectId}/{invoiceId}/)
      try {
        const { data: list0 } = await supabase?.storage?.from('sub-invoices')?.list(`${projectId}/${invoiceId}`);
        if (list0 && list0?.length > 0) {
          const file0 = list0?.find(f => f?.name && !f?.name?.startsWith('.')) || list0?.[0];
          if (file0?.name) {
            const { data: s0 } = await supabase?.storage?.from('sub-invoices')?.createSignedUrl(`${projectId}/${invoiceId}/${file0?.name}`, 3600);
            if (s0?.signedUrl) return s0?.signedUrl;
          }
        }
      } catch (_e) {
        // fall through
      }

      // 1) contracts-documents (FALLBACK — legacy location)
      try {
        const { data: list } = await supabase?.storage?.from('contracts-documents')?.list(`sub_invoices/${invoiceId}`);
        console.log('[approve-pdf] contracts list for', invoiceId, '=', list);
        if (list && list?.length > 0) {
          const file = list?.find(f => f?.name && !f?.name?.startsWith('.')) || list?.[0];
          const path = `sub_invoices/${invoiceId}/${file?.name}`;
          const { data: s } = await supabase?.storage?.from('contracts-documents')?.createSignedUrl(path, 3600);
          if (s?.signedUrl) return s?.signedUrl;
        }
      } catch (_e) {
        // fall through
      }

      // 2) invoice-inbox (FALLBACK)
      try {
        const { data: inboxRows } = await supabase?.from('invoice_inbox')?.select('id, file_path, file_name, file_url')?.eq('linked_invoice_id', invoiceId)?.order('created_at', { ascending: false })?.limit(1);
        const inboxRow = inboxRows?.[0];
        if (inboxRow) {
          if (inboxRow?.file_name) setFileName(inboxRow?.file_name);
          if (inboxRow?.file_url && inboxRow?.file_url?.startsWith('http')) {
            return inboxRow?.file_url;
          } else if (inboxRow?.file_path) {
            const { data: s2 } = await supabase?.storage?.from('invoice-inbox')?.createSignedUrl(inboxRow?.file_path, 3600);
            if (s2?.signedUrl) return s2?.signedUrl;
          }
        }
      } catch (_e) {
        // fall through
      }

      return null;
    };

    const load = async () => {
      setLoading(true);
      try {
        // Load the facture
        const inv = await subInvoiceService?.getByProjectId(projectId);
        const found = inv?.find(i => i?.id === invoiceId);
        if (found) {
          setFacture(found);
          setNoteValue(found?.notes || '');
          // Pre-fill approvedAmount to 2 decimal places
          const total = found?.invoiceTotalWithTax ?? 0;
          setApprovedAmount(String(Math.round(total * 100) / 100));
          // Use invoice number as fallback file name
          if (found?.invoiceNumber) setFileName(found?.invoiceNumber + '.pdf');
        }

        // Resolve document URL via cascade
        const absoluteUrl = await resolveDocumentUrl(invoiceId);
        console.log('[approve-pdf] resolvedDocUrl =', absoluteUrl);
        setResolvedDocUrl(absoluteUrl);
      } catch (err) {
        console.error('[FactureApprovalPage] load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [invoiceId, projectId]);

  // Vérifie l'existence d'une quittance finale SIGNÉE (lettre_url présente) pour
  // le même project_id + subcontract_id. Sans elle, la libération est bloquée.
  useEffect(() => {
    const checkQuittanceFinale = async () => {
      if (!isReleaseMode) { setQuittanceFinaleOk(null); return; }
      if (!factureSubcontractId) { setQuittanceFinaleOk(false); return; }
      try {
        const { data } = await supabase
          ?.from('quittances')
          ?.select('id')
          ?.eq('project_id', projectId)
          ?.eq('subcontract_id', factureSubcontractId)
          ?.eq('type', 'finale')
          ?.not('lettre_url', 'is', null)
          ?.limit(1);
        setQuittanceFinaleOk((data?.length || 0) > 0);
      } catch (_e) {
        setQuittanceFinaleOk(false);
      }
    };
    checkQuittanceFinale();
  }, [isReleaseMode, factureSubcontractId, projectId]);

  const handleReleaseRetention = async () => {
    setError('');
    setReleasing(true);
    try {
      await subInvoiceService?.releaseRetention(invoiceId);
      navigate(`/project/${projectId}/sub-invoices`);
    } catch (err) {
      console.error('[FactureApprovalPage] release error:', err);
      setError(err?.message || t('factureApproval.retentionRelease.error'));
      setReleasing(false);
      setShowReleaseConfirm(false);
    }
  };

  // Facture rattachee a une DP que le client n'a pas encore payee.
  const dpNotPaid = Boolean(facture?.paymentRequest) && facture?.paymentRequest?.status !== 'paid';

  const handleApprove = async () => {
    setError('');
    // Validate the amount that will actually be approved (gross at intake, else editable)
    const amountToCheck = hasEntryRetention ? grossApproved : approvedAmt;
    if (!amountToCheck || amountToCheck <= 0) {
      setError(t('subInvoices.approveModal.errorApprove') || 'Le montant approuvé doit être supérieur à 0.');
      return;
    }

    setSaving(true);
    try {
      // 1. Save the edited note
      await subInvoiceService?.update(invoiceId, { notes: noteValue }, null);

      // 2. Approve — retention_included reuses the intake values as-is (no second decision)
      const approvalPayload = hasEntryRetention
        ? {
            approvedAmount: grossApproved,
            retainagePercent: intakeRetentionPct,
            retainageAmount: intakeRetentionAmount,
            netPayable: intakeNetPayable,
            paymentNote: noteValue,
            clientPaymentRequestNumber: facture?.paymentRequest?.number ?? null,
          }
        : {
            approvedAmount: approvedAmt,
            retainagePercent: applyRetention ? retentionPctNum : 0,
            paymentNote: noteValue,
            clientPaymentRequestNumber: facture?.paymentRequest?.number ?? null,
          };
      await subInvoiceService?.approve(invoiceId, approvalPayload, null);

      // 3. Navigate back
      navigate(`/project/${projectId}/sub-invoices`);
    } catch (err) {
      console.error('[FactureApprovalPage] approve error:', err);
      setError(err?.message || t('subInvoices.approveModal.errorApprove'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!facture) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm text-gray-500">{t('factureApproval.notFound') || 'Facture introuvable.'}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] overflow-hidden">
      {/* 2-pane body */}
      <div className="flex flex-1 overflow-hidden gap-3 p-3">
        {/* LEFT: PDF Viewer ~60% */}
        <div className="w-[60%] flex flex-col h-full overflow-hidden">
          {/* PDF card header — mirrors invoice-inbox */}
          <div className="flex items-center justify-between px-4 py-3 mb-2 bg-white border border-gray-200 rounded-xl shadow-sm flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                <Icon name="FileText" size={14} className="text-primary" />
              </div>
              <span className="text-sm font-medium text-gray-800 truncate">
                {t('factureApproval.invoiceLabel') || 'Facture'} #{facture?.invoiceNumber || '—'}
                {facture?.subcontractor?.companyName ? ` — ${facture?.subcontractor?.companyName}` : ''}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {facture?.status && (
                <Badge variant={statusVariant('subInvoices', facture?.status)}>
                  {t(`subInvoices.status.${facture?.status}`)}
                </Badge>
              )}
              <button
                onClick={() => navigate(`/project/${projectId}/sub-invoices`)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>
          {resolvedDocUrl ? (
            <NativePdfViewer
              preloadedUrl={resolvedDocUrl}
              fileName={fileName}
              filePath={resolvedDocUrl}
              fileUrl={resolvedDocUrl}
              t={t}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-3 bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                <Icon name="FileX" size={24} className="text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-500">
                {t('factureApproval.noPdf') || 'Aucun PDF lié'}
              </p>
              <p className="text-xs text-gray-400">
                {t('factureApproval.noPdfHint') || 'Aucun document source trouvé pour cette facture.'}
              </p>
            </div>
          )}
        </div>

        {/* RIGHT: Controls ~40% */}
        <div className="w-[40%] h-full overflow-hidden">
          <div className="bg-white rounded-xl border border-gray-200 h-full flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

            {/* A) Résumé READ-ONLY */}
            <div>
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {t('factureApproval.summary') || 'Résumé'}
              </h2>
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">{t('subInvoices.table.subcontractor') || 'Sous-traitant'}</span>
                  <span className="font-medium text-gray-900">{facture?.subcontractor?.companyName || '—'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">{t('subInvoices.approveModal.invoiceNumber') || 'N° facture'}</span>
                  <span className="font-medium text-gray-900">{facture?.invoiceNumber || '—'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">{t('factureApproval.invoiceDate') || 'Date'}</span>
                  <span className="font-medium text-gray-900">{fmtDate(facture?.invoiceDate)}</span>
                </div>
                <div className="flex justify-between text-xs border-t border-gray-200 pt-1.5 mt-1">
                  <span className="text-gray-500">{t('factureApproval.subtotal') || 'Sous-total'}</span>
                  <span className="font-medium text-gray-900">{fmt(facture?.subtotalAmount ?? facture?.invoiceTotal)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">{t('factureApproval.tps') || 'TPS'}</span>
                  <span className="font-medium text-gray-900">{fmt(facture?.tpsAmount ?? (facture?.invoiceTotal * TPS_RATE))}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">{t('factureApproval.tvq') || 'TVQ'}</span>
                  <span className="font-medium text-gray-900">{fmt(facture?.tvqAmount ?? (facture?.invoiceTotal * TVQ_RATE))}</span>
                </div>
                <div className="flex justify-between text-xs border-t border-gray-200 pt-1.5 mt-1">
                  <span className="font-semibold text-gray-700">{t('factureApproval.totalTaxIn') || 'Total (taxes incl.)'}</span>
                  <span className="font-bold text-gray-900">{fmt(facture?.invoiceTotalWithTax)}</span>
                </div>
                <div className="flex justify-between text-xs border-t border-gray-200 pt-1.5 mt-1">
                  <span className="text-gray-500">{t('factureApproval.linkedDp') || 'DP liée'}</span>
                  <span className="font-medium text-gray-900">
                    {facture?.paymentRequest?.number != null ? `DP-${facture?.paymentRequest?.number}` : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* B) Rappel retenue — informational badge only */}
            <div>
              {facture?.retentionIncluded === true ? (
                <div className="flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <Icon name="Info" size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700">
                    {t('factureApproval.retentionIncludedBadge') || 'Retenue incluse par le sous-traitant à la saisie'}
                  </p>
                </div>
              ) : (
                <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <Icon name="AlertCircle" size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    {t('factureApproval.retentionExcludedBadge') || 'Retenue exclue à la saisie'}
                  </p>
                </div>
              )}
            </div>

            {/* D) Contrôles d'approbation */}
            <div>
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {isReleaseMode
                  ? t('factureApproval.retentionRelease.title')
                  : (t('factureApproval.approvalControls') || "Contrôles d'approbation")}
              </h2>
              <div className="space-y-3">

                {isReleaseMode ? (
                  /* Mode « libération de la retenue » — lecture seule + gate quittance */
                  <div className="bg-orange-50 rounded-lg border border-orange-200 p-3 space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">{t('subInvoices.approveModal.approvedAmount') || 'Montant approuvé'}</span>
                      <span className="font-medium text-gray-900">{fmt(releaseApprovedAmount)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">{t('subInvoices.paymentModal.alreadyPaid') || 'Déjà payé'}</span>
                      <span className="font-medium text-gray-900">{fmt(releaseAlreadyPaid)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">{t('factureApproval.retentionRelease.retentionToRelease')}</span>
                      <span className="font-medium text-orange-700">{fmt(releaseRetenue)}</span>
                    </div>
                    <div className="flex justify-between text-xs border-t border-orange-200 pt-1">
                      <span className="font-semibold text-gray-700">{t('factureApproval.retentionRelease.remaining')}</span>
                      <span className="font-bold text-orange-700">{fmt(releaseReste)}</span>
                    </div>
                    {!factureSubcontractId ? (
                      <div className="flex items-start gap-2 mt-2 px-2.5 py-2 bg-red-50 border border-red-200 rounded-lg">
                        <Icon name="AlertCircle" size={13} className="text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-red-700">{t('factureApproval.retentionRelease.noContract')}</p>
                      </div>
                    ) : quittanceFinaleOk === false ? (
                      <div className="mt-2 px-2.5 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Icon name="AlertCircle" size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-700">{t('factureApproval.retentionRelease.quittanceRequired')}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate(`/project/${projectId}/quittances`)}
                          className="mt-1.5 ml-5 text-xs font-medium text-primary hover:text-primary-dark underline"
                        >
                          {t('factureApproval.retentionRelease.quittanceLink')}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : hasEntryRetention ? (
                  /* Read-only box — retenue déjà appliquée à la saisie (Boîte de réception) */
                  <div className="bg-blue-50 rounded-lg border border-blue-100 p-3 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">{t('subInvoices.approveModal.approvedAmount') || 'Montant approuvé'}</span>
                      <span className="font-medium text-gray-900">{fmt(grossApproved)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">{t('subInvoices.approveModal.retainage') || 'Retenue'} ({intakeRetentionPct}%)</span>
                      <span className="font-medium text-red-600">{intakeRetentionAmount > 0 ? `−${fmt(intakeRetentionAmount)}` : '—'}</span>
                    </div>
                    <div className="flex justify-between text-xs border-t border-blue-100 pt-1">
                      <span className="font-semibold text-gray-700">{t('subInvoices.approveModal.netPayable') || 'Net à payer'}</span>
                      <span className="font-bold text-green-700">{fmt(intakeNetPayable)}</span>
                    </div>
                  </div>
                ) : (
                <>
                {/* Montant approuvé — editable, pre-filled to 2 decimals */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('subInvoices.approveModal.approvedAmount') || 'Montant approuvé'}
                  </label>
                  {/* Quick % selector — writes the $ field below (base: total incl. taxes) */}
                  <div className="flex items-center gap-2 mb-2">
                    {[90, 95, 100].map(pct => (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => applyApprovedPct(String(pct))}
                        className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                          isActivePct(pct)
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {pct}%
                      </button>
                    ))}
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={pctInput}
                        onChange={e => applyApprovedPct(e?.target?.value)}
                        placeholder="%"
                        className="w-20 text-xs border border-gray-200 rounded-md pl-2 pr-5 py-1 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">%</span>
                    </div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={approvedAmount}
                    onChange={e => setApprovedAmount(e?.target?.value)}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  />
                </div>

                {/* Retenue — single lever: toggle + % input */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      id="apply-retention-toggle"
                      type="checkbox"
                      checked={applyRetention}
                      onChange={e => setApplyRetention(e?.target?.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer"
                    />
                    <label htmlFor="apply-retention-toggle" className="text-xs font-medium text-gray-700 cursor-pointer">
                      {t('factureApproval.applyRetentionLabel') || 'Appliquer la retenue'}
                    </label>
                  </div>
                  {applyRetention && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('factureApproval.retentionPctLabel') || 'Retenue (%)'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={retentionPct}
                        onChange={e => setRetentionPct(e?.target?.value)}
                        className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                      />
                    </div>
                  )}
                </div>

                {/* Computed summary */}
                <div className="bg-blue-50 rounded-lg border border-blue-100 p-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">{t('subInvoices.approveModal.approvedAmount') || 'Montant approuvé'}</span>
                    <span className="font-medium text-gray-900">{fmt(approvedAmt)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">
                      {t('subInvoices.approveModal.retainage') || 'Retenue'} ({applyRetention ? retentionPctNum : 0}%)
                    </span>
                    <span className={`font-medium ${applyRetention && retainageAmount > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {applyRetention && retainageAmount > 0 ? `−${fmt(retainageAmount)}` : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs border-t border-blue-100 pt-1">
                    <span className="font-semibold text-gray-700">{t('subInvoices.approveModal.netPayable') || 'Net à payer'}</span>
                    <span className="font-bold text-green-700">{fmt(netPayable)}</span>
                  </div>
                </div>
                </>
                )}

                {/* Error */}
                {error && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}
              </div>
            </div>

            {/* C) Note EDITABLE */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('factureApproval.noteLabel') || 'Note'}
              </label>
              <textarea
                value={noteValue}
                onChange={e => setNoteValue(e?.target?.value)}
                rows={3}
                placeholder={t('factureApproval.notePlaceholder') || 'Ajouter une note...'}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none transition-colors"
              />
            </div>
          </div>
          {/* La DP de cette facture n'est pas encore payee par le client : avertissement seulement, l'approbation reste possible. */}
          {dpNotPaid && !isReleaseMode && (
            <div className="mx-5 mb-3 bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-start gap-2 flex-shrink-0">
              <span className="text-yellow-600 text-sm mt-0.5">⚠️</span>
              <p className="text-xs font-semibold text-yellow-800">
                {t('subInvoices.approveModal.dpNotPaid', { number: facture?.paymentRequest?.number })}
              </p>
            </div>
          )}
          {/* Footer bar */}
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-3 flex-shrink-0">
            <button
              onClick={() => navigate(`/project/${projectId}/sub-invoices`)}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              {t('factureApproval.back') || 'Retour'}
            </button>
            {isReleaseMode ? (
              <button
                onClick={() => setShowReleaseConfirm(true)}
                disabled={releasing || quittanceFinaleOk !== true}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {releasing ? (
                  <>
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    {t('factureApproval.retentionRelease.releasing')}
                  </>
                ) : (
                  <>
                    <Icon name="Coins" size={14} />
                    {t('factureApproval.retentionRelease.approveButton')}
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleApprove}
                disabled={saving}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-xl transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    {t('subInvoices.approveModal.approving') || 'Approbation...'}
                  </>
                ) : (
                  <>
                    <Icon name="CheckCircle" size={14} />
                    {dpNotPaid ? t('subInvoices.approveModal.approveAnyway') : t('factureApproval.approveButton') || 'Approuver à payer'}
                  </>
                )}
              </button>
            )}
          </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showReleaseConfirm}
        icon="Coins"
        title={t('factureApproval.retentionRelease.confirmTitle')}
        message={t('factureApproval.retentionRelease.confirmMessage')}
        confirmLabel={t('factureApproval.retentionRelease.confirmButton')}
        cancelLabel={t('factureApproval.back')}
        loading={releasing}
        onConfirm={handleReleaseRetention}
        onCancel={() => setShowReleaseConfirm(false)}
      />
    </div>
  );
};

export default FactureApprovalPage;
